import { useEditor, useNode } from "@craftjs/core";
import * as React from "react";
import sanitizeHtml from "sanitize-html";
import type { Pattern, TextAlign } from "src/types";

import PrimitivesFloatingOptions from "@/components/editor/block-outline/floating-options/primitives-floating-component";
import { Typography } from "@/components/editor/shared/typography";
import { swallowEvent } from "@/components/event-boundary/event-boundary-component";

import { StyledContentEditable } from "./text-component-styles";

export type TextStyleProps = {
  fontSize?: string;
  fontWeight?: number;
  fontFamily?: string;
  color?: string;
  textAlign?: TextAlign;
};

type InternalTextComponentProps = {
  _text?: string;
  _inheritFontIndex?: number;
};

// Do not allow any styles in contenteditable
const sanitizeConfig = {
  allowedTags: ["br", "div"],
  allowedAttributes: {},
};

export type TextComponentProps = {
  text?: string;
  element?: any; // h1, h2, h3 – we can type this better
  onChange?: (value: string) => void;
  style?: TextStyleProps;
  /**
   * `inheritFontIndex` the index of type `Font` to use from the `FontProvider`
   */
  inheritFontIndex?: number;
  pattern?: Pattern; // TODO: remove question mark when updated
} & InternalTextComponentProps;

/**
 * `Text` – a primitive block which renders a contenteditable.
 * We want to place `Text` blocks within user components
 */
export default function Text(props: TextComponentProps) {
  const {
    connectors: { connect },
    selected,
    actions: { setProp },
  } = useNode((node) => ({
    nodeId: node.id,
    selected: node.events.selected,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const [editable, setEditable] = React.useState(false);

  React.useEffect(() => {
    if (!selected) {
      setEditable(false);
    }
  }, [selected]);

  const handleChange = React.useCallback(
    (event) => {
      const sanitizedValue = sanitizeHtml(event.target.value, sanitizeConfig);
      const replaced = sanitizedValue
        .replace(/( )<br \/>$/, "&nbsp;") // Take care of Firefox issue with adding extra <br />s for trailing spaces
        .replace(/^(.)<br \/>/, "$1"); // Take care of Firefox's cursor jumping when deleting everything and beginning to type again
      setProp((prop) => (prop._text = replaced));
    },
    [setProp]
  );

  const handleClick = React.useCallback(() => setEditable(true), [setEditable]);

  const {
    text,
    _text,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    inheritFontIndex,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _inheritFontIndex,
    pattern,
    ...rest
  } = props;

  return (
    <Typography
      {...rest}
      pattern={pattern}
      innerRef={connect}
      onKeyDown={swallowEvent}
      disabled={!editable || !enabled}
      html={typeof _text === "undefined" ? text : _text}
      onClick={handleClick}
      onChange={handleChange}
      tagName={props.element || "p"}
      className={`text-primitive ${pattern}`}
      as={StyledContentEditable}
    />
  );
}

Text.craft = {
  custom: {
    displayName: "Text",
  },
  related: {
    leftOutlineOptions: PrimitivesFloatingOptions,
  },
};
